import {
  Alert,
  Form,
  FormProps,
  Icons,
  Input,
  InputNumber,
  Modal,
  ModalProps,
  Radio,
  Select,
} from '@pankod/refine-antd'
import { useOne } from '@pankod/refine-core'
import React, { useEffect, useState } from 'react'

import ContentForm from '@components/ContentBank/ContentForm'
import { TFilterOptions } from '@components/DataTableParticipants/types'
import { InfiniteSelect } from '@resources/lms-management/components/InfiniteSelect'
import { ShowcaseLandingPageRequest } from '@resources/lms-management/landing-page/types'
import PreviewDetailLandingPage from '@resources/lms-management/landing-page/blocks/PreviewDetailLandingPage'
import {
  sanitizeLandingPageTargeting,
  validateWhitelistEmailDomains,
} from '@resources/lms-management/landing-page/utils'

type ShowcaseModalFormProps = {
  modalProps: ModalProps
  formProps: FormProps
  mode: 'create' | 'edit'
  onClose: () => void
  onSubmit?: () => Promise<ShowcaseLandingPageRequest>
}

const defaultBlueprintData = {
  imageUrl: '',
  title: '-',
  description: '-',
}

const ShowcaseModalForm = ({
  formProps,
  modalProps,
  mode,
  onClose,
  onSubmit,
}: ShowcaseModalFormProps) => {
  const isEdit = mode === 'edit'
  const [blueprintData, setBlueprintData] = useState(defaultBlueprintData)

  useEffect(() => {
    if (formProps?.initialValues?.blueprint?.title && modalProps.visible) {
      setBlueprintData({
        title: formProps?.initialValues?.blueprint?.title,
        description: formProps?.initialValues?.blueprint?.description,
        imageUrl: formProps?.initialValues?.image || '',
      })
    }
  }, [formProps?.initialValues, modalProps.visible])

  const category = Form.useWatch('category', formProps.form)

  const { data: filterOptions } = useOne<TFilterOptions>({
    resource: 'programs/participants',
    id: 'filter-options',
    dataProviderName: 'lms',
    queryOptions: {
      enabled: !!modalProps.visible,
    },
  })

  const provinceOptions =
    filterOptions?.data?.regions
      ?.map((province) => ({
        label: province.name,
        value: province.kode,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) || []

  return (
    <Modal
      {...modalProps}
      title={isEdit ? 'Edit Showcase' : 'Tambah Showcase Baru'}
      onCancel={() => {
        onClose()
        formProps.form?.resetFields()
      }}
      okText="Simpan"
      cancelText="Batal"
      maskClosable={false}
      forceRender
      okButtonProps={{
        ...modalProps.okButtonProps,
        icon: <Icons.SaveOutlined />,
        onClick: isEdit
          ? async () => {
              const data = await onSubmit?.()
              formProps.onFinish?.(data)
            }
          : modalProps.okButtonProps?.onClick,
      }}
    >
      <div className="flex gap-6">
        <div className="w-full">
          <Form
            {...formProps}
            onFinish={(values) =>
              formProps.onFinish?.(sanitizeLandingPageTargeting(values))
            }
            size="small"
            layout="vertical"
            validateMessages={{ required: '${label} harus diisi.' }}
            initialValues={{
              blueprintCourseId: formProps?.initialValues?.blueprint?.courseId,
              ...formProps?.initialValues,
            }}
          >
            <div className="grid grid-cols-2 gap-2">
              <Form.Item
                label="Blueprint"
                name="blueprintCourseId"
                rules={[{ required: true }]}
              >
                <InfiniteSelect
                  disabled={isEdit}
                  optionLabel="title"
                  optionValue="courseId"
                  resource="diklat/blueprints"
                  visible={modalProps.visible}
                  searchParams="nameOrCourseCode"
                  onSelect={(data) => setBlueprintData(data?.data)}
                  placeholder="Pilih blueprint"
                  initialOptionValue={
                    isEdit
                      ? {
                          label: formProps?.initialValues?.blueprint?.title,
                          value: formProps?.initialValues?.blueprint?.courseId,
                        }
                      : undefined
                  }
                />
              </Form.Item>
              <Form.Item
                label="Urutan"
                name="sortOrder"
                rules={[{ required: true }]}
                initialValue={1}
              >
                <InputNumber
                  placeholder="Masukkan angka urutan"
                  className="!w-full"
                />
              </Form.Item>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Form.Item label="Judul" name="title">
                  <Input placeholder="Tulis judul" />
                </Form.Item>
                <Form.Item label="Deskripsi" name="description">
                  <Input.TextArea
                    rows={2}
                    className="resize-none"
                    placeholder="Tulis deskripsi"
                  />
                </Form.Item>
              </div>
              <div>
                <Form.Item
                  label="Judul Default"
                  help="Judul ini didapat dari blueprint"
                >
                  <Input disabled value={blueprintData.title} />
                </Form.Item>
                <Form.Item
                  label="Deskripsi Default"
                  help="Deskripsi ini didapat dari blueprint"
                >
                  <Input.TextArea
                    rows={2}
                    className="resize-none"
                    disabled
                    value={blueprintData.description}
                  />
                </Form.Item>
              </div>
            </div>
            <Form.Item
              label="Link URL"
              name="url"
              rules={[
                {
                  type: 'url',
                  message: 'Format link url tidak valid',
                  validateTrigger: [],
                },
              ]}
            >
              <Input
                placeholder="Tulis link url"
                onBlur={(e) => {
                  const value = e.target.value?.trim()
                  if (
                    value &&
                    !value.startsWith('http://') &&
                    !value.startsWith('https://')
                  ) {
                    formProps.form?.setFieldsValue({ url: `https://${value}` })
                  }
                  formProps.form?.validateFields(['url'])
                }}
              />
            </Form.Item>
            <Form.Item label="Detail Diklat" name="detail">
              <ContentForm
                index={isEdit ? 'edit' : 'create'}
                showTitle={false}
                showDeleteButton={false}
                showIndentation={false}
                showHeader={[
                  { label: 'Title', value: '4' },
                  { label: 'Normal', value: '' },
                ]}
                editorClassName="max-h-[260px] overflow-auto"
              />
            </Form.Item>
            <div className="grid grid-cols-2 gap-2">
              <Form.Item
                label="Teks Status"
                name="status"
                rules={[{ required: true }]}
              >
                <Input placeholder="Tulis teks untuk status" />
              </Form.Item>
              <Form.Item label="Teks Badge" name="badge">
                <Input placeholder="Tulis teks pada badge" />
              </Form.Item>
            </div>
            <Form.Item
              label="Kategori Tampilan"
              name="category"
              initialValue="PUBLIC"
              rules={[{ required: true }]}
            >
              <Radio.Group>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Radio value="PUBLIC">Publik</Radio>
                  <Radio value="PROVINCE">Provinsi Tertentu</Radio>
                  <Radio value="WHITELIST_USER">
                    User Tertentu (<em>Whitelisted</em>)
                  </Radio>
                  <Radio value="WHITELIST_EMAIL_DOMAIN">
                    Domain Email (<em>Whitelisted</em>)
                  </Radio>
                </div>
              </Radio.Group>
            </Form.Item>

            {category === 'PROVINCE' && (
              <Form.Item
                label="Pilih Provinsi"
                name="provinceCodes"
                rules={[
                  {
                    required: true,
                    message: 'Harus memilih setidaknya satu provinsi',
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  placeholder="Pilih provinsi"
                  placement="topRight"
                  options={provinceOptions}
                  filterOption={(input, option) =>
                    (option?.label as unknown as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            )}

            {category === 'WHITELIST_USER' && (
              <>
                <Form.Item
                  label="Email User Whitelist"
                  name="whitelistUsers"
                  rules={[
                    {
                      required: true,
                      message: 'Harus mengisi setidaknya satu email',
                    },
                    {
                      validator: async (_, value) => {
                        if (!value || value.length === 0)
                          return Promise.resolve()
                        // Basic email regex
                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
                        const invalidEmails = value.filter(
                          (email: string) => !emailRegex.test(email),
                        )

                        if (invalidEmails.length > 0) {
                          return Promise.reject(
                            new Error(
                              `Email tidak valid: ${invalidEmails.join(', ')}`,
                            ),
                          )
                        }
                        return Promise.resolve()
                      },
                    },
                  ]}
                >
                  <Select
                    mode="tags"
                    placeholder="Ketik email dan tekan enter"
                    tokenSeparators={[',', ' ']}
                    open={false}
                    allowClear
                  />
                </Form.Item>
                <Alert
                  type="info"
                  message={
                    <>
                      Anda dapat <em>paste</em> daftar email yang dipisahkan
                      dengan koma.
                    </>
                  }
                />
              </>
            )}

            {category === 'WHITELIST_EMAIL_DOMAIN' && (
              <>
                <Form.Item
                  label="Domain Email Whitelist"
                  name="whitelistEmailDomains"
                  rules={[
                    {
                      required: true,
                      message: 'Harus mengisi setidaknya satu domain email',
                    },
                    {
                      validator: validateWhitelistEmailDomains,
                    },
                  ]}
                >
                  <Select
                    mode="tags"
                    placeholder="Ketik domain email dan tekan enter"
                    tokenSeparators={[',', ' ']}
                    open={false}
                    allowClear
                  />
                </Form.Item>
                <Alert
                  type="info"
                  message={
                    <>
                      Setiap domain email harus diawali dengan &quot;@&quot;.
                      Anda dapat <em>paste</em> daftar domain yang dipisahkan
                      dengan koma.
                    </>
                  }
                />
              </>
            )}
          </Form>
        </div>
        <div>
          <PreviewDetailLandingPage
            form={formProps?.form}
            blueprintData={blueprintData}
          />
        </div>
      </div>
    </Modal>
  )
}

export default ShowcaseModalForm
